/* Creator's Friend — the rail.

   Three jobs: open and close the panel on a phone, keep the cart count
   honest, and survive the theme editor re-rendering the section.

   Deliberately not a web component and deliberately not deferred behind
   a framework: the rail is the site's navigation and has to work on the
   first paint, including when something else on the page has thrown. */
(function () {
  "use strict";

  var MOBILE = "(max-width: 1023px)";

  function init(rail) {
    if (rail.dataset.railReady) return;
    rail.dataset.railReady = "1";

    var toggle = rail.querySelector("[data-rail-toggle]");
    var panel  = rail.querySelector("[data-rail-panel]");
    if (!toggle || !panel) return;

    function isOpen() { return panel.classList.contains("is-open"); }

    function setOpen(open) {
      panel.classList.toggle("is-open", open);
      toggle.setAttribute("aria-expanded", String(open));
      /* The page behind a full-height panel should not scroll under it.
         Locking <html> rather than <body> because the theme already
         writes to body for its own drawer, and two owners of one style
         is how a page ends up permanently unscrollable. */
      document.documentElement.style.overflow = open ? "hidden" : "";
    }

    toggle.addEventListener("click", function () {
      var next = !isOpen();
      setOpen(next);
      if (next) {
        var first = panel.querySelector("a");
        if (first) first.focus({ preventScroll: true });
      }
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && isOpen()) {
        setOpen(false);
        toggle.focus({ preventScroll: true });
      }
    });

    document.addEventListener("click", function (e) {
      if (isOpen() && !rail.contains(e.target)) setOpen(false);
    });

    /* Rotating a phone, or dragging a desktop window narrow and back,
       must not leave the panel hidden-but-open: at desktop widths the
       stylesheet forces it visible, so an open flag there would strand
       `overflow:hidden` on the document with nothing to close. */
    var mq = window.matchMedia(MOBILE);
    var onChange = function (e) { if (!e.matches) setOpen(false); };
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);

    /* ---- where the rail starts -------------------------------------------
       The rail is fixed, so it has no idea what is above it. Measure the
       sections that precede it inside its own group and hand the total
       to CSS. Re-measured on resize because the announcement bar wraps
       to two lines on a narrow desktop window, and on section:load
       because the editor re-renders it while you watch. */
    function setTop() {
      var host = rail.closest(".shopify-section") || rail.parentElement;
      var total = 0;
      var sib = host && host.previousElementSibling;
      while (sib) {
        if (sib.offsetParent !== null || sib.getBoundingClientRect().height) {
          total += sib.getBoundingClientRect().height;
        }
        sib = sib.previousElementSibling;
      }
      rail.style.setProperty("--rail-top", Math.round(total) + "px");
    }
    setTop();
    window.addEventListener("resize", setTop);
    document.addEventListener("shopify:section:load", setTop);
    if (window.ResizeObserver) {
      var host = rail.closest(".shopify-section");
      var above = host && host.previousElementSibling;
      if (above) new ResizeObserver(setTop).observe(above);
    }

    /* ---- opening the cart drawer ----------------------------------------
       `data-cart-toggle` alone does NOT open Broadcast's drawer. The theme
       binds it in `header-component.cartToggleEvent()`, and that method
       reads `this.querySelectorAll("[data-cart-toggle]")` — scoped to the
       header component, not the document. This store's header section is
       disabled, so <header-component> never renders, nothing binds, and a
       cart marked up the theme's way just follows its href to /cart.

       So the rail does for its own carts exactly what that method does for
       the header's, step for step:

         const s = document.querySelector("cart-drawer");
         s && (e.preventDefault(),
               s.dispatchEvent(new CustomEvent("theme:cart-drawer:show")),
               window.a11y.lastElement = t)

       Three conditions are the theme's, not ours, and each one has a
       correct fallback rather than a failure:

       - cartType must be "drawer". Set Cart type to Page in theme settings
         and there is no drawer to open; the href is then the right answer.
       - <cart-drawer> must exist. It is absent on /cart by design
         (`template.name != 'cart'` in the section), so the link navigates.
       - The theme's own JS must have run. If it threw, the href still works.

       Nothing here is a guess about an internal API: `theme:cart-drawer:show`
       is the event <cart-drawer> binds on itself in connectedCallback, and
       `window.a11y` is the global theme.js creates. If Broadcast renames
       either, the cart stops opening the drawer and starts going to /cart —
       visibly wrong, not silently broken.

       The panel is closed BEFORE the drawer opens, so the burger menu is
       not left sitting behind it to be rediscovered when the drawer shuts,
       and so the two do not both own the page's scroll lock. */
    var drawerCart = window.theme
      && window.theme.settings
      && window.theme.settings.cartType === "drawer";

    [].forEach.call(rail.querySelectorAll("[data-cart-toggle]"), function (a) {
      a.addEventListener("click", function (e) {
        if (isOpen()) setOpen(false);
        if (!drawerCart) return;
        var drawer = document.querySelector("cart-drawer");
        if (!drawer) return;
        e.preventDefault();
        drawer.dispatchEvent(new CustomEvent("theme:cart-drawer:show"));
        if (window.a11y) window.a11y.lastElement = a;
      });
    });

    /* ---- the country picker ---------------------------------------------
       A <select> inside Shopify's localization form. It submits on change
       rather than sitting behind a Go button — there is one field, so a
       second click to confirm the only choice you just made is a step for
       nothing. */
    var country = rail.querySelector("[data-rail-country]");
    if (country) {
      country.addEventListener("change", function () {
        var form = country.closest("form");
        if (form) form.submit();
      });
    }

    /* ---- the cart count ------------------------------------------------
       Rendered by Liquid on first paint, so it is right before any script
       runs. After that the theme adds to the cart without a reload, so
       listen for the events Broadcast already fires and re-read the cart
       rather than trying to guess the delta. */
    // querySelectorAll, not querySelector. The cart is rendered twice —
    // once in the bar for a phone, once in the panel for a desktop — and
    // only one of them is visible at a time. Taking the first would leave
    // the count on whichever one the viewer can actually see frozen at
    // whatever Liquid printed on first paint, and it would look right in
    // testing exactly half the time.
    var counts = rail.querySelectorAll("[data-rail-count]");
    if (counts.length) {
      var refresh = function () {
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        fetch(root + "cart.js", { headers: { Accept: "application/json" } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (cart) {
            if (cart && typeof cart.item_count === "number") {
              [].forEach.call(counts, function (c) { c.textContent = cart.item_count; });
            }
          })
          .catch(function () { /* a stale count beats a broken rail */ });
      };
      ["theme:cart:change", "theme:cart:refresh", "cart:refresh", "cart:build"]
        .forEach(function (name) { document.addEventListener(name, refresh); });
    }
  }

  function boot(scope) {
    (scope || document).querySelectorAll("[data-rail]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { boot(); });
  } else {
    boot();
  }
  /* The editor re-renders a section without reloading the page, which is
     exactly when someone is looking at it. */
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
  document.addEventListener("shopify:section:unload", function () {
    document.documentElement.style.overflow = "";
  });
})();